import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, startOfWeek, addDays } from "date-fns";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, Copy, Home as HomeIcon, Plane, ArrowLeft } from "lucide-react";
import * as htmlToImage from "html-to-image";
import api, { formatApiError } from "@/lib/api";

// Facebook / Instagram weekly poster generator.
//
// Four variants (matched to how the club posts on social):
//   • Youth home       — U6 → U18, games we host on our pitches
//   • Youth away       — U6 → U18, games at opponent grounds (FA fixtures)
//   • Adults home      — Vets & Men's, games we host
//   • Adults away      — Vets & Men's, at opponent grounds
//
// Each variant renders as one 1080×1350 Instagram-portrait sheet. The social
// committee taps "Copy image" (paste straight into a post) or "Download PNG".
// Data comes from GET /fixtures/club with a week-window filter (from/to) —
// no new backend endpoint needed. Home rows are club bookings
// (session_type=game); away rows are the FA-synced fixtures.
//
// Vets/Men's split: any team whose age_group is exactly "Adults" is treated
// as adult. Everything else (U6–U18) is youth. Vets vs Men's is disambiguated
// by the team name (which shows on each poster line).

const YOUTH_AGES = new Set(["U6","U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18"]);
const isAdult = (r) => r.age_group === "Adults";
const isYouth = (r) => YOUTH_AGES.has(r.age_group);

const DAY_LABEL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const VARIANTS = [
  { key: "youth-home",  audience: "youth",  location: "home", label: "Youth · Home",        icon: HomeIcon, tone: "sage" },
  { key: "youth-away",  audience: "youth",  location: "away", label: "Youth · Away",        icon: Plane,    tone: "blue" },
  { key: "adults-home", audience: "adults", location: "home", label: "Vets & Men's · Home", icon: HomeIcon, tone: "sage" },
  { key: "adults-away", audience: "adults", location: "away", label: "Vets & Men's · Away", icon: Plane,    tone: "blue" },
];

const AUDIENCE_TITLE = { youth: "Youth", adults: "Vets & Men's" };
const LOC_TITLE = { home: "Home", away: "Away" };

export default function SocialPosters() {
  const nav = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [variant, setVariant] = useState("youth-home");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const posterRef = useRef(null);

  const weekEnd = addDays(weekStart, 6);
  const fromISO = format(weekStart, "yyyy-MM-dd");
  const toISO = format(weekEnd, "yyyy-MM-dd");
  const active = VARIANTS.find(v => v.key === variant);

  useEffect(() => {
    setLoading(true);
    api.get("/fixtures/club", { params: { location: active.location, from: fromISO, to: toISO } })
      .then(({ data }) => setRows(data.fixtures || []))
      .catch(err => toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load fixtures"))
      .finally(() => setLoading(false));
  }, [variant, fromISO, toISO, active.location]);

  // Filter by audience client-side (backend doesn't know Youth vs Adults;
  // it only knows each team's age_group).
  const filteredRows = useMemo(
    () => rows.filter(active.audience === "youth" ? isYouth : isAdult),
    [rows, active.audience],
  );

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const k = r.date || "TBC";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return [...map.entries()].sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0));
  }, [filteredRows]);

  const shiftWeek = (days) => setWeekStart(prev => addDays(prev, days));

  const downloadPoster = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await htmlToImage.toPng(posterRef.current, {
        pixelRatio: 2, cacheBust: true, backgroundColor: "#0A0F1C",
      });
      const link = document.createElement("a");
      link.download = `EYFC-${variant}-${fromISO}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("PNG downloaded — ready to post");
    } catch {
      toast.error("Couldn't render the poster image");
    } finally {
      setBusy(false);
    }
  };

  const copyPoster = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      const blob = await htmlToImage.toBlob(posterRef.current, {
        pixelRatio: 2, cacheBust: true, backgroundColor: "#0A0F1C",
      });
      // Clipboard.write with an image blob works in Chrome, Edge and modern Safari.
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      toast.success("Copied — paste straight into Facebook or Instagram");
    } catch {
      toast.error("Couldn't copy — try Download instead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8" data-testid="social-posters-page">
      <div className="mb-5">
        <button onClick={() => nav(-1)} className="text-xs opacity-60 hover:opacity-100 mb-3 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</button>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Social committee</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Weekly Facebook + Instagram posters</h1>
        <p className="opacity-70 text-sm mt-1 max-w-2xl">Pick a variant, jump to the week, then either <strong>Copy image</strong> (paste straight into the post) or <strong>Download PNG</strong> — the sheet is sized for Instagram portrait 1080×1350.</p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={()=>shiftWeek(-7)} data-testid="social-prev-week" className="p-2 rounded border border-border hover:border-[#83B68B]"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={()=>setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="social-this-week" className="text-xs tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B]">This week</button>
          <button onClick={()=>shiftWeek(7)} data-testid="social-next-week" className="p-2 rounded border border-border hover:border-[#83B68B]"><ChevronRight className="w-4 h-4" /></button>
          <span className="text-xs opacity-70 ml-2">Wk of <strong>{format(weekStart, "d MMM yyyy")}</strong></span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyPoster} disabled={busy} data-testid="social-copy" className="text-[11px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5 disabled:opacity-50">
            <Copy className="w-3 h-3" /> Copy image
          </button>
          <button onClick={downloadPoster} disabled={busy} data-testid="social-download" className="text-[11px] tracking-widest uppercase px-4 py-2 rounded bg-[#83B68B] text-white hover:bg-[#6ba173] inline-flex items-center gap-1.5 disabled:opacity-50">
            <Download className="w-3 h-3" /> Download PNG
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        {VARIANTS.map(v => {
          const Icon = v.icon;
          const activeV = variant === v.key;
          return (
            <button
              key={v.key}
              onClick={() => setVariant(v.key)}
              data-testid={`social-variant-${v.key}`}
              className={`px-3 py-2 rounded-md text-xs tracking-widest uppercase inline-flex items-center gap-2 justify-center transition-colors ${
                activeV
                  ? (v.tone === "sage" ? "bg-[#83B68B]/15 text-[#83B68B] border border-[#83B68B]/50" : "bg-blue-500/15 text-blue-300 border border-blue-500/50")
                  : "border border-border opacity-70 hover:opacity-100"
              }`}
            >
              <Icon className="w-3 h-3" /> {v.label}
            </button>
          );
        })}
      </div>

      <div className="flex justify-center overflow-hidden">
        <PosterSheet
          ref={posterRef}
          audience={active.audience}
          location={active.location}
          weekStart={weekStart}
          weekEnd={weekEnd}
          grouped={grouped}
          loading={loading}
        />
      </div>
    </div>
  );
}

// The sheet renders at native 1080×1350 (Instagram portrait). We wrap it in
// a fixed-size preview container that visually scales it down to 540×675 on
// screen; when we call html-to-image on the ref, it captures the un-scaled
// element at pixelRatio 2 so the exported PNG is a crisp 2160×2700.
const PosterSheet = forwardRef(function PosterSheet(
  { audience, location, weekStart, weekEnd, grouped, loading },
  ref,
) {
  const isAway = location === "away";
  const accent = isAway ? "#60A5FA" : "#83B68B";
  return (
    <div style={{ width: 540, height: 675, position: "relative" }}>
      <div
        ref={ref}
        style={{
          width: 1080,
          height: 1350,
          transform: "scale(0.5)",
          transformOrigin: "top left",
          background: "#0A0F1C",
          color: "#FFFFFF",
          fontFamily: "'Inter', system-ui, sans-serif",
          padding: "70px 70px 60px 70px",
          boxSizing: "border-box",
          position: "absolute",
          top: 0,
          left: 0,
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: 0, left: 0, width: 220, height: 8, background: accent }} />
        <div style={{ position: "absolute", bottom: 0, right: 0, width: 220, height: 8, background: accent }} />

        <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 24 }}>
          <img src="/logo.jpg" alt="EYFC" style={{ width: 92, height: 92, objectFit: "contain", background: "#fff", borderRadius: 8, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1 }}>ELSENHAM YFC</div>
            <div style={{ fontSize: 15, letterSpacing: 4, textTransform: "uppercase", color: "rgba(255,255,255,.6)", marginTop: 8 }}>
              Est · Train · Play
            </div>
          </div>
        </div>

        <div style={{ height: 4, background: accent, marginBottom: 30, opacity: 0.9 }} />

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 34 }}>
          <div>
            <div style={{ fontSize: 20, letterSpacing: 5, textTransform: "uppercase", color: accent }}>
              {AUDIENCE_TITLE[audience]} · {LOC_TITLE[location]}
            </div>
            <div style={{ fontSize: 72, fontWeight: 900, lineHeight: 1, marginTop: 12 }}>
              {isAway ? "Away Days" : "Home Fixtures"}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,.55)" }}>Week of</div>
            <div style={{ fontSize: 30, fontWeight: 800, marginTop: 4, whiteSpace: "nowrap" }}>{format(weekStart, "d MMM")}</div>
            <div style={{ fontSize: 16, color: "rgba(255,255,255,.55)", marginTop: 2, whiteSpace: "nowrap" }}>— {format(weekEnd, "d MMM yyyy")}</div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {loading ? (
            <div style={{ opacity: 0.55, fontSize: 20 }}>Loading…</div>
          ) : grouped.length === 0 ? (
            <div style={{
              border: `2px solid ${accent}`,
              borderRadius: 12,
              padding: "44px 32px",
              textAlign: "center",
              background: "rgba(255,255,255,.02)",
            }}>
              <div style={{ fontSize: 28, fontWeight: 900 }}>No {isAway ? "away" : "home"} fixtures this week</div>
              <div style={{ fontSize: 16, color: "rgba(255,255,255,.6)", marginTop: 10 }}>Check back next week</div>
            </div>
          ) : (
            grouped.slice(0, 6).map(([date, dayRows]) => (
              <div key={date}>
                <div style={{ fontSize: 15, letterSpacing: 5, textTransform: "uppercase", color: accent, fontWeight: 700, marginBottom: 8 }}>
                  {DAY_LABEL[parseISO(date).getDay()]} · {format(parseISO(date), "d MMM")}
                </div>
                {dayRows.slice(0, 6).map((r, i, arr) => (
                  <div key={`${r.origin}-${r.booking_id || r.fa_id}-${i}`} style={{
                    display: "flex", alignItems: "baseline", gap: 16,
                    padding: "10px 0",
                    borderBottom: i === arr.length - 1 ? "none" : "1px solid rgba(255,255,255,.08)",
                  }}>
                    <div style={{ fontWeight: 900, fontSize: 24, width: 88, flexShrink: 0, color: "#FFF" }}>{r.kickoff || "TBC"}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
                        <span>{r.team_name || "TBC"}</span>
                        {r.opponent && <span style={{ color: "rgba(255,255,255,.55)", fontWeight: 500 }}> vs </span>}
                        {r.opponent && <span>{r.opponent}</span>}
                      </div>
                      <div style={{ fontSize: 14, color: "rgba(255,255,255,.55)", marginTop: 3 }}>
                        {r.age_group && <span>{r.age_group}</span>}
                        {r.venue && <span> · {r.venue}</span>}
                        {r.match_type && <span> · {r.match_type}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <div style={{
          position: "absolute", bottom: 60, left: 70, right: 70,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid rgba(255,255,255,.15)", paddingTop: 22,
          fontSize: 14, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,.55)",
        }}>
          <span>@elsenhamyouthfc</span>
          <span>Come and support the boys & girls</span>
        </div>
      </div>
    </div>
  );
});
